import Big from 'big.js';
import {combineLatest, merge} from 'rxjs';
import {throttleTime, filter, map, scan, shareReplay, startWith} from 'rxjs/operators';
import {DEFAULT_USD_PRICE_FACTOR, DEFAULT_USD_PRICE_SCALE} from '@/const/data-config';
import {numUtil} from '@/helper/num';
import {spotsCache$, contractsCache$, contractsIndexSymbolCache$} from '@/config';
import {tickMsg$} from '@/raw/tick';
import {contractMarket24hData$} from '@/raw/market24h-contract';
import {spotMarket24hData$} from '@/raw/market24h-spot';
import type {ws, ContractPair, SpotPair, PriceEntity, TickPriceEntity} from '@/types';

type ContractMarketData = ws.ContractMarketData;
type SpotMarketData = ws.SpotMarketData;

const contractMarket24hIndexPrices$ = combineLatest([contractMarket24hData$, contractsCache$]).pipe(
  map(convertContractMarket24hIndexPrices),
  filter((list: TickPriceEntity[]) => list.length > 0)
);

const spotMarket24hIndexPrices$ = combineLatest([spotMarket24hData$, spotsCache$]).pipe(
  map(convertSportMarket24hIndexPrices),
  filter((list: TickPriceEntity[]) => list.length > 0)
);

const tickIndexPrices$ = combineLatest([tickMsg$, contractsIndexSymbolCache$]).pipe(
  map(([tickData, contractsIndexSymbolCache]): TickPriceEntity[] => [
    convertTickIndexPrice(tickData, contractsIndexSymbolCache),
  ])
);

export const indexPricesCache$ = merge<TickPriceEntity[][]>(
  tickIndexPrices$,
  contractMarket24hIndexPrices$,
  spotMarket24hIndexPrices$
).pipe(
  startWith([{symbol: '.USD', priceEp: DEFAULT_USD_PRICE_FACTOR, price: '1'}]), // 添加 USD，统一使用方法
  scan((p, c: TickPriceEntity[]) => {
    c.forEach(({symbol, priceEp, price}: TickPriceEntity) => {
      p.set(symbol, {priceEp, price, priceFactor: 1e4, priceScale: 4});
    });
    return p;
  }, new Map<string, PriceEntity>()),
  throttleTime(1000),
  shareReplay(1)
);

function convertContractMarket24hIndexPrices([contractMarket24hDataList, contractMap]: [
  ContractMarketData[],
  Map<string, ContractPair>
]): TickPriceEntity[] {
  return contractMarket24hDataList
    .map((o: ContractMarketData) => convertContractMarket24hIndexPrice(o, contractMap))
    .filter(Boolean);
}

function convertContractMarket24hIndexPrice(
  contractMarket24hData: ContractMarketData,
  contractMap: Map<string, ContractPair>
): TickPriceEntity {
  const contract = contractMap.get(contractMarket24hData.symbol);
  if (!contract) {
    return null;
  }

  const priceEp = Big(contractMarket24hData.indexPrice)
    .times(Big(10).pow(DEFAULT_USD_PRICE_SCALE - contract.priceScale))
    .toNumber();

  return {
    symbol: contract.indexSymbol,
    priceEp,
    price: numUtil.floor(contractMarket24hData.indexPrice, contract.pricePrecision, contract.priceScale),
  };
}

function convertSportMarket24hIndexPrices([spotMarket24hData, spotsCache]: [
  SpotMarketData[],
  Map<string, SpotPair>
]): TickPriceEntity[] {
  return spotMarket24hData.map((o: SpotMarketData) => convertSportMarket24hIndexPrice(o, spotsCache)).filter(Boolean);
}

function convertSportMarket24hIndexPrice(
  spotMarket24hData: SpotMarketData,
  spotsCache: Map<string, SpotPair>
): TickPriceEntity {
  const spot = spotsCache.get(spotMarket24hData.symbol);
  if (!spot) {
    return null;
  }
  if (!['USDT', 'MUSDT'].includes(spot.quoteCurrency)) {
    /**
     * quoteCurrency 不为 USDT 时，推送的market[9]的价格指数也不是USD，因此也不可作为 baseCurrency 的价格
     */
    return null;
  }

  const priceEp = Big(spotMarket24hData.indexEp)
    .times(Big(10).pow(DEFAULT_USD_PRICE_SCALE - spot.quoteQtyScale))
    .toNumber();

  return {
    symbol: spot.indexSymbol,
    priceEp,
    price: numUtil.floor(spotMarket24hData.indexEp, spot.pricePrecision, spot.quoteQtyScale),
  };
}

function convertTickIndexPrice({tick}, contractsIndexSymbolCache: Map<string, ContractPair>): TickPriceEntity {
  const {symbol, last, scale} = tick;
  const priceEp = last * Math.pow(10, DEFAULT_USD_PRICE_SCALE - scale);
  const contract = contractsIndexSymbolCache.get(symbol);
  const pricePrecision = calcPricePrecision(contract);
  return {symbol, priceEp, price: numUtil.floor(priceEp, pricePrecision, DEFAULT_USD_PRICE_SCALE)};
}

function calcPricePrecision(contract): number {
  if (contract) {
    return contract.pricePrecision;
  }

  // 默认为 2, 包括 .USDT
  return 2;
}
